import { useId, useMemo, useState, type ChangeEvent, type FormEvent, type ReactElement } from 'react';
import {
  FEEDBACK_MAX_FILES,
  FEEDBACK_MAX_PHOTO_BYTES,
  FEEDBACK_MAX_TEXT_LENGTH,
  FEEDBACK_MAX_TOTAL_FILE_BYTES,
  FEEDBACK_MAX_VIDEO_BYTES,
  feedbackDocumentText,
  type FeedbackKind,
  type FeedbackRichTextDocument,
} from '../../server/feedback/contracts.js';
import { Alert, Button, Modal, PanelHeader, RichTextField, Select } from '../ui/kit/index.js';
import { PixelIcon } from '../ui/PixelIcon.js';

const EMPTY_DOCUMENT: FeedbackRichTextDocument = { blocks: [] };
const ALLOWED_PHOTOS = new Set(['image/jpeg', 'image/png', 'image/webp']);
const ALLOWED_VIDEOS = new Set(['video/mp4', 'video/webm', 'video/quicktime']);

interface FeedbackResponse {
  readonly ok?: unknown;
  readonly attachmentsFailed?: unknown;
}

function fileSize(bytes: number): string {
  if (bytes < 1_024 * 1_024) return `${Math.max(1, Math.round(bytes / 1_024))} КБ`;
  return `${(bytes / (1_024 * 1_024)).toFixed(1)} МБ`;
}

function attachmentError(files: readonly File[]): string | undefined {
  if (files.length > FEEDBACK_MAX_FILES) return `Можно прикрепить не больше ${FEEDBACK_MAX_FILES} файлов.`;
  let total = 0;
  for (const file of files) {
    total += file.size;
    if (ALLOWED_PHOTOS.has(file.type)) {
      if (file.size > FEEDBACK_MAX_PHOTO_BYTES) return `Фото «${file.name}» больше 10 МБ.`;
      continue;
    }
    if (ALLOWED_VIDEOS.has(file.type)) {
      if (file.size > FEEDBACK_MAX_VIDEO_BYTES) return `Видео «${file.name}» больше 20 МБ.`;
      continue;
    }
    return `Файл «${file.name}» не является поддерживаемым фото или видео.`;
  }
  return total > FEEDBACK_MAX_TOTAL_FILE_BYTES ? 'Все вложения вместе должны быть не больше 30 МБ.' : undefined;
}

function responseError(status: number): string {
  if (status === 413) return 'Описание или вложения превышают допустимый размер.';
  if (status === 429) return 'Слишком много отправок. Попробуйте через несколько минут.';
  if (status === 502) return 'Telegram временно не принял сообщение. Попробуйте ещё раз.';
  return 'Не удалось отправить сообщение. Проверьте соединение и попробуйте ещё раз.';
}

export function FeedbackButton(): ReactElement {
  const titleId = useId();
  const descriptionId = useId();
  const fileInputId = useId();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<FeedbackKind>('bug');
  const [document, setDocument] = useState<FeedbackRichTextDocument>(EMPTY_DOCUMENT);
  const [files, setFiles] = useState<readonly File[]>([]);
  const [fileError, setFileError] = useState<string>();
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'partial'>('idle');
  const [error, setError] = useState<string>();
  const [resetKey, setResetKey] = useState(0);
  const textLength = useMemo(() => [...feedbackDocumentText(document)].length, [document]);

  const close = (): void => {
    if (status !== 'sending') setOpen(false);
  };

  const chooseFiles = (event: ChangeEvent<HTMLInputElement>): void => {
    const selected = [...(event.currentTarget.files ?? [])];
    event.currentTarget.value = '';
    const next = [...files, ...selected].filter((file, index, all) => all.findIndex((candidate) =>
      candidate.name === file.name
      && candidate.size === file.size
      && candidate.lastModified === file.lastModified) === index);
    const problem = attachmentError(next);
    if (problem !== undefined) {
      setFileError(problem);
      return;
    }
    setFileError(undefined);
    setFiles(next);
    setError(undefined);
    setStatus('idle');
  };

  const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const text = feedbackDocumentText(document);
    if (text.length === 0 || textLength > FEEDBACK_MAX_TEXT_LENGTH || fileError !== undefined) return;
    setStatus('sending');
    setError(undefined);
    const body = new FormData();
    body.set('kind', kind);
    body.set('document', JSON.stringify(document));
    body.set('page', `${window.location.pathname}${window.location.search}`);
    body.set('userAgent', navigator.userAgent);
    files.forEach((file) => body.append('files', file, file.name));
    try {
      const response = await fetch('/api/feedback', { method: 'POST', body });
      if (!response.ok) {
        setError(responseError(response.status));
        setStatus('idle');
        return;
      }
      const payload = await response.json() as FeedbackResponse;
      const attachmentsFailed = typeof payload.attachmentsFailed === 'number'
        ? payload.attachmentsFailed
        : 0;
      setStatus(attachmentsFailed > 0 ? 'partial' : 'sent');
      setDocument(EMPTY_DOCUMENT);
      setFiles([]);
      setFileError(undefined);
      setResetKey((value) => value + 1);
    } catch {
      setError('Не удалось связаться с сервером. Проверьте соединение и попробуйте ещё раз.');
      setStatus('idle');
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="compact"
        className="auth-feedback-trigger"
        data-testid="feedback-open"
        title="Фидбек"
        onClick={() => {
          setOpen(true);
          setError(undefined);
          if (status === 'sent' || status === 'partial') setStatus('idle');
        }}
      >
        <PixelIcon name="feedback" className="auth-menu-icon" />
        <span className="auth-menu-label">Фидбек</span>
      </Button>

      {open ? (
        <Modal
          as="section"
          layer="detail"
          className="feedback-modal"
          labelledBy={titleId}
          busy={status === 'sending'}
          testId="feedback-modal"
          onClose={close}
        >
          <PanelHeader
            className="feedback-modal-header"
            title="Обратная связь"
            headingLevel={2}
            headingId={titleId}
            description="Расскажите об ошибке или предложите улучшение. Сообщение и вложения уйдут напрямую разработчику."
            actions={(
              <Button type="button" variant="ghost" size="sm" aria-label="Закрыть форму" onClick={close}>×</Button>
            )}
          />

          <form className="feedback-form" onSubmit={(event) => { void submit(event); }}>
            <div className="feedback-form-body">
            <label className="feedback-field">
              <span className="feedback-label">Тип сообщения</span>
              <Select
                value={kind}
                disabled={status === 'sending'}
                onChange={(event) => setKind(event.currentTarget.value as FeedbackKind)}
              >
                <option value="bug">Ошибка</option>
                <option value="idea">Предложение</option>
                <option value="other">Другое</option>
              </Select>
            </label>

            <label className="feedback-field">
              <span className="feedback-label">Описание</span>
              <RichTextField
                resetKey={resetKey}
                invalid={textLength > FEEDBACK_MAX_TEXT_LENGTH}
                aria-describedby={`${descriptionId}-counter`}
                onChange={(value) => {
                  setDocument(value);
                  setError(undefined);
                  if (status === 'sent' || status === 'partial') setStatus('idle');
                }}
              />
              <span
                id={`${descriptionId}-counter`}
                className="feedback-counter"
                data-invalid={textLength > FEEDBACK_MAX_TEXT_LENGTH ? 'true' : undefined}
              >
                {textLength}/{FEEDBACK_MAX_TEXT_LENGTH}
              </span>
            </label>

            <div className="feedback-field">
              <span className="feedback-label">Фото и видео</span>
              <div className="feedback-file-row">
                <label className="feedback-file-picker" htmlFor={fileInputId}>Прикрепить файлы</label>
                <span>до {FEEDBACK_MAX_FILES} файлов, всего до 30 МБ</span>
              </div>
              {/* Native file input owns the OS photo/video picker; kit text fields cannot express it. */}
              <input
                id={fileInputId}
                className="feedback-file-input"
                type="file"
                accept="image/jpeg,image/png,image/webp,video/mp4,video/webm,video/quicktime"
                multiple
                disabled={status === 'sending'}
                onChange={chooseFiles}
              />
              {files.length === 0 ? null : (
                <ul className="feedback-files" aria-label="Прикреплённые файлы">
                  {files.map((file, index) => (
                    <li key={`${file.name}:${file.size}:${file.lastModified}`}>
                      <span title={`${file.name} · ${fileSize(file.size)}`}>{file.name}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="compact"
                        aria-label={`Убрать ${file.name}`}
                        disabled={status === 'sending'}
                        onClick={() => setFiles((current) => current.filter((_, currentIndex) => currentIndex !== index))}
                      >
                        ×
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {fileError === undefined ? null : <Alert tone="error">{fileError}</Alert>}
            {error === undefined ? null : <Alert tone="error">{error}</Alert>}
            {status === 'sent' ? <Alert tone="success" role="status">Спасибо! Сообщение доставлено.</Alert> : null}
            {status === 'partial' ? (
              <Alert tone="warning" role="status">Текст доставлен, но часть вложений Telegram не принял.</Alert>
            ) : null}
            </div>

            <Modal.Actions className="feedback-actions">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={status === 'sending'}
                onClick={close}
              >
                Отмена
              </Button>
              <Button
                type="submit"
                size="sm"
                disabled={status === 'sending' || textLength === 0
                  || textLength > FEEDBACK_MAX_TEXT_LENGTH || fileError !== undefined}
              >
                {status === 'sending' ? 'Отправляем…' : 'Отправить'}
              </Button>
            </Modal.Actions>
          </form>
        </Modal>
      ) : null}
    </>
  );
}
